/**
 * Product Detail Tabs Interactive Functionality
 * Handles tab switching for product detail pages with multiple tab wrappers
 */

class ProductDetailTabs {
  constructor() {
    this.tabWrappers = document.querySelectorAll(".product-detail-tab-wrapper");
    this.init();
  }

  init() {
    if (this.tabWrappers.length === 0) return;

    this.tabWrappers.forEach((wrapper) => {
      this.initializeTabWrapper(wrapper);
    });
  }

  initializeTabWrapper(wrapper) {
    const menuItems = wrapper.querySelectorAll(".product-detail-tab-menu-item");
    const contentItems = wrapper.querySelectorAll(
      ".product-detail-tab-content-item"
    );

    // Add click event listeners to menu items
    menuItems.forEach((menuItem) => {
      menuItem.addEventListener("click", (e) => {
        e.preventDefault();
        this.switchTab(wrapper, menuItem, menuItems, contentItems);
      });

      // Add keyboard support
      menuItem.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.switchTab(wrapper, menuItem, menuItems, contentItems);
        }
      });

      // Make tab items focusable
      menuItem.setAttribute("tabindex", "0");
      menuItem.setAttribute("role", "tab");
    });

    // Set ARIA attributes for accessibility
    this.setupAccessibility(wrapper, menuItems, contentItems);
  }

  switchTab(wrapper, clickedMenuItem, allMenuItems, allContentItems) {
    const tabId = clickedMenuItem.getAttribute("data-tab-id");
    const position = wrapper.getAttribute("data-tab-position");

    // Update menu items
    allMenuItems.forEach((menuItem) => {
      const isActive = menuItem.getAttribute("data-tab-id") === tabId;
      menuItem.classList.toggle("active", isActive);
      menuItem.setAttribute("data-tab-active", isActive ? "1" : "0");
      menuItem.setAttribute("aria-selected", isActive);
    });

    // Update content items with animation
    allContentItems.forEach((contentItem) => {
      const isActive = contentItem.getAttribute("data-tab-id") === tabId;

      if (isActive) {
        // Show the new active content with animation
        contentItem.classList.add("active");
        contentItem.setAttribute("data-tab-active", "1");
        contentItem.setAttribute("aria-hidden", "false");

        // Add fade-in animation
        setTimeout(() => {
          contentItem.classList.add("fade-in");
        }, 10);
      } else {
        // Hide inactive content
        contentItem.classList.remove("active", "fade-in");
        contentItem.setAttribute("data-tab-active", "0");
        contentItem.setAttribute("aria-hidden", "true");
      }
    });

    // Dispatch custom event for other components to listen
    const event = new CustomEvent("tabChanged", {
      detail: {
        tabId: tabId,
        position: position,
        wrapper: wrapper,
      },
    });
    wrapper.dispatchEvent(event);

    // Update URL hash if needed (optional)
    this.updateUrlHash(tabId, position);
  }

  setupAccessibility(wrapper, menuItems, contentItems) {
    // Set up ARIA attributes
    const menu = wrapper.querySelector(".product-detail-tab-menu");
    const content = wrapper.querySelector(".product-detail-tab-content");

    if (menu) {
      menu.setAttribute("role", "tablist");
    }

    if (content) {
      content.setAttribute("role", "tabpanel");
    }

    // Set initial ARIA states
    menuItems.forEach((menuItem) => {
      const tabId = menuItem.getAttribute("data-tab-id");
      const isActive = menuItem.classList.contains("active");

      menuItem.setAttribute("aria-selected", isActive);
      menuItem.setAttribute("aria-controls", `tab-content-${tabId}`);
      menuItem.setAttribute("id", `tab-${tabId}`);
    });

    contentItems.forEach((contentItem) => {
      const tabId = contentItem.getAttribute("data-tab-id");
      const isActive = contentItem.classList.contains("active");

      contentItem.setAttribute("aria-labelledby", `tab-${tabId}`);
      contentItem.setAttribute("id", `tab-content-${tabId}`);
      contentItem.setAttribute("aria-hidden", !isActive);
    });
  }

  updateUrlHash(tabId, position) {
    // Optional: Update URL hash to maintain tab state on page refresh
    if (window.history && window.history.replaceState) {
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set("tab", tabId);
      currentUrl.searchParams.set("position", position);
      window.history.replaceState({}, "", currentUrl);
    }
  }

  // Method to programmatically switch to a specific tab
  switchToTab(position, tabId) {
    const wrapper = document.querySelector(`[data-tab-position="${position}"]`);
    if (!wrapper) return false;

    const menuItem = wrapper.querySelector(`[data-tab-id="${tabId}"]`);
    if (!menuItem) return false;

    const menuItems = wrapper.querySelectorAll(".product-detail-tab-menu-item");
    const contentItems = wrapper.querySelectorAll(
      ".product-detail-tab-content-item"
    );

    this.switchTab(wrapper, menuItem, menuItems, contentItems);
    return true;
  }

  // Method to get current active tab for a position
  getActiveTab(position) {
    const wrapper = document.querySelector(`[data-tab-position="${position}"]`);
    if (!wrapper) return null;

    const activeMenuItem = wrapper.querySelector(
      ".product-detail-tab-menu-item.active"
    );
    return activeMenuItem ? activeMenuItem.getAttribute("data-tab-id") : null;
  }

  // Method to initialize tabs from URL parameters
  initializeFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const tabId = urlParams.get("tab");
    const position = urlParams.get("position");

    if (tabId && position) {
      this.switchToTab(position, tabId);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window.productDetailTabs = new ProductDetailTabs();

  // Initialize from URL parameters if present
  window.productDetailTabs.initializeFromUrl();
});

// Export for use in other scripts
export default ProductDetailTabs;
